import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import CloseIcon from "../../assets/img/closeIcon.svg";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  emailPrefix,
  getInitials,
  resolveParticipantName,
  getApplicationTitle,
} from "../../helper/applicationHelpers";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveCreatorId = (created_by) => {
  if (!created_by) return null;
  if (typeof created_by === "string") return created_by;
  return created_by._id ?? null;
};

/* ── role badge ── */
const RoleBadge = ({ role, _t }) => {
  switch (role) {
    case "owner":
      return (
        <span className="app-member-badge app-member-badge-owner">
          {_t("owner", "Owner")}
        </span>
      );
    case "invited":
      return (
        <span className="app-member-badge app-member-badge-invited">
          {_t("invited", "Invited")}
        </span>
      );
    case "accepted":
      return (
        <span className="app-member-badge app-member-badge-accepted">
          {_t("accepted", "Accepted")}
        </span>
      );
    case "pending":
      return (
        <span className="app-member-badge app-member-badge-invited">
          {_t("pending", "Pending")}
        </span>
      );
    case "rejected":
      return (
        <span className="app-member-badge app-member-badge-rejected">
          {_t("rejected", "Rejected")}
        </span>
      );
    default:
      return null;
  }
};

/* ── single application card ── */
const ApplicationCard = ({
  app,
  currentUserEmail,
  currentUserId,
  onAccept,
  onReject,
  onDelete,
  processingId,
  openMenuId,
  setOpenMenuId,
  onCloseModal,
  llShowOnly,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);
  const isProcessing = processingId === app._id;
  const menuOpen = openMenuId === app._id;

  const participants = app.participants ?? [];
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const creatorId = resolveCreatorId(app.created_by);
  const iAmOwner =
    (creatorId && creatorId === currentUserId) ||
    (creatorEmail && creatorEmail.toLowerCase() === currentUserEmail);

  const isPendingInvitation =
    app.isPendingInvitation ??
    (!iAmOwner &&
      participants.some(
        (p) =>
          p.email?.toLowerCase() === currentUserEmail &&
          p.consent_status === "pending"
      ));

  const pendingCount = participants.filter(
    (p) =>
      p.consent_status === "pending" &&
      p.email?.toLowerCase() !== creatorEmail?.toLowerCase()
  ).length;

  const shareRequests = app.shareRequests ?? [];
  const sharedProfiles = (app.sharedProfiles ?? []).filter(
    (sharedProfile) => sharedProfile?.id?._id
  );
  const totalShared = shareRequests.length + sharedProfiles.length;

  /* status badge under the card header */
  const getStatusBadge = () => {
    if (isPendingInvitation) {
      const name = resolveCreatorName(app.created_by);
      return (
        <span className="application-status-badge application-badge-invitation">
          {_t("invitationFrom")} {name}
        </span>
      );
    }

    if (pendingCount > 0) {
      return (
        <span className="application-status-badge application-badge-pending">
          {_t("pendingConsent")} &bull; {pendingCount}{" "}
          {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
        </span>
      );
    }

    if (totalShared > 0) {
      return (
        <span className="application-status-badge application-badge-shared">
          {_t("sharedWithApplication")} {totalShared}{" "}
          {totalShared === 1 ? _t("landlordApp") : _t("landlordsApp")}
        </span>
      );
    }

    return null;
  };

  /* determine the role of a single participant */
  const getRole = (participant) => {
    const isOwnerParticipant =
      creatorEmail &&
      participant.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwnerParticipant) return "owner";

    const isMe = participant.email?.toLowerCase() === currentUserEmail;
    if (isMe && participant.consent_status === "pending") return "invited";

    return participant.consent_status ?? "pending";
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  return (
    <div
      onClick={() => {
        setExpanded((v) => !v);
        setOpenMenuId(null);
      }}
      className={`application-card cursor-pointer${isPendingInvitation ? " application-card-invitation" : ""}`}
    >
      {/* ── header row ── */}
      <div className="application-card-row">
        <div className="application-card-icon-wrap">
          <div className="d-flex align-items-center custom-gap-3 position-relative">
            <div className="earner-thumbnail-wrapper earner-thumbnail-wrapper-px d-flex align-items-center justify-content-center">
              <img src="/images/Flatmates.svg" alt="User Icon" />
            </div>
            {participants?.length > 0 && (
              <div className="member-count-wrapper d-flex align-items-center justify-content-center">
                {participants.length}
              </div>
            )}
          </div>
        </div>

        <div className="application-card-info">
          <div className="application-card-name-row">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <p
                className="application-card-name"
                title={getApplicationTitle(app, _t)}
              >
                {getApplicationTitle(app, _t)}
              </p>
              {app.skor_protect && <SkorProtectBadge />}
            </div>
            <div className="application-avatars">
              {participants.slice(0, 4).map((p, i) => {
                const name = resolveParticipantName(p);
                const isPending = p.consent_status === "pending";
                return (
                  <div key={i} className="member-avatar-chip-wrap">
                    <div
                      className={`member-avatar-chip member-avatar-chip-gray position-relative ${isPending ? "member-avatar-chip-pending" : ""}`}
                      title={name}
                      style={{ zIndex: i }}
                    >
                      {getInitials(name)}
                      {isPending && (
                        <span className="member-chip-warn">
                          <img src="/images/warning-dark-yellow.svg" alt="" />
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              {participants.length > 4 && (
                <div className="member-avatar-chip member-avatar-chip-gray">
                  +{participants.length - 4}
                </div>
              )}
            </div>
          </div>
          {/* <p className="application-card-members">
            {participants.length} {_t("members")}
          </p> */}
        </div>

        <div className="application-card-controls">
          <button
            className="application-chevron-btn"
            // onClick={() => setExpanded((v) => !v)}
            aria-label="toggle"
          >
            {expanded ? (
              <KeyboardArrowUpIcon fontSize="small" />
            ) : (
              <KeyboardArrowDownIcon fontSize="small" />
            )}
          </button>

          {isPendingInvitation ? (
            <>
              <button
                className="application-reject-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(app._id);
                }}
                disabled={isProcessing}
                aria-label="reject"
              >
                <CloseRoundedIcon fontSize="small" />
              </button>
              <button
                className="application-accept-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(app._id);
                }}
                disabled={isProcessing}
                aria-label="accept"
              >
                <CheckIcon fontSize="small" />
              </button>
            </>
          ) : !llShowOnly ? (
            <div className="position-relative" ref={menuRef}>
              <MoreVertIcon
                className="cursor-pointer"
                style={{ color: "#141B34" }}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenMenuId(menuOpen ? null : app._id);
                }}
              />
              {menuOpen && (
                <div className="editDeleteDropdown share-profile-three-dot-modal">
                  <p
                    className="custom-gap-2"
                    style={{ color: "#141B34" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(null);
                      onCloseModal();
                      const url = new URL(window.location.href);
                      url.searchParams.set("shareprofilemodal", "true");
                      url.searchParams.set("applicationId", app._id);
                      window.history.replaceState(
                        window.history.state,
                        "",
                        `${url.pathname}${url.search}${url.hash}`
                      );
                      window.dispatchEvent(
                        new Event("open-share-profile-modal")
                      );
                    }}
                  >
                    <img src="/images/send-req.svg" alt=""></img>
                    {_t("shareApplicationWithLandlord", "Share with landlord")}
                  </p>
                  <p
                    className={`deleteText custom-gap-2${isProcessing ? " processing" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isProcessing) {
                        setOpenMenuId(null);
                        onDelete(app._id);
                      }
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <img
                          src={SkorDefaultLoader}
                          className="delete-loader"
                          alt=""
                        ></img>
                        {_t("deleting", "Deleting...")}
                      </>
                    ) : (
                      <>
                        <img src="/images/delete-icon.svg" alt=""></img>
                        {_t("deleteApplication", "Delete application")}
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      {/* ── status badge + avatars ── */}
      <div className="application-card-footer">
        {!llShowOnly && getStatusBadge()}
      </div>

      {/* ── expanded: tenants list ── */}
      {expanded && (
        <>
          {participants.length > 0 && (
            <div className="application-expanded-members">
              <p className="application-tenants-label">
                {_t("tenantsInThisApplication", "TENANTS IN THIS APPLICATION")}
              </p>
              {participants.map((p, i) => {
                const role = getRole(p);
                const isMe = p.email?.toLowerCase() === currentUserEmail;
                const isOwnerRow = role === "owner";
                const hasTenantId = p.id && llShowOnly;
                const canNavigate = hasTenantId && !isMe;

                /* display name: always show "You" when it's the current user */
                const displayName = isMe
                  ? _t("you", "You")
                  : resolveParticipantName(p);

                /* subtitle: "Owner" label for owner row, otherwise email */
                const subtitle = isOwnerRow
                  ? _t("owner", "Owner")
                  : (p.email ?? "");

                return (
                  <div
                    key={i}
                    className={`application-member-row${canNavigate ? " cursor-pointer" : ""}`}
                    onClick={() => {
                      if (canNavigate) {
                        const url = new URL(window.location.href);
                        url.searchParams.set("tenant_id", p.id);
                        window.history.pushState({}, "", url.toString());
                        window.dispatchEvent(new PopStateEvent("popstate"));
                      }
                    }}
                  >
                    <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                      {getInitials(isMe ? "Yo" : resolveParticipantName(p))}
                    </div>
                    <div className="application-member-info">
                      <p className="application-member-name">{displayName}</p>
                      <p className="application-member-email">{subtitle}</p>
                    </div>
                    <RoleBadge role={role} _t={_t} />
                    {canNavigate && (
                      <ChevronRightIcon
                        fontSize="small"
                        style={{ color: "#141B34", marginLeft: "8px" }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── expanded: shared with ── */}
          {totalShared > 0 && (
            <div className="application-expanded-members">
              <p className="application-tenants-label">
                {_t("sharedWithSection", "SHARED WITH")}
              </p>

              {shareRequests.map((sr, i) => {
                const landlord = sr.landlord_id ?? {};
                const sharedBy = sr.tenant_id ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName =
                  `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                  sharedBy.email ||
                  "";
                return (
                  <div key={sr._id ?? i} className="application-member-row">
                    <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                      {getInitials(landlordName || landlordEmail)}
                    </div>
                    <div className="application-member-info">
                      <p className="application-member-name">{landlordName}</p>
                      <p className="application-member-email">
                        {landlordEmail}
                      </p>
                    </div>
                    <div className="app-shared-by-stack">
                      {sharedByName && (
                        <span className="app-shared-by-label">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      )}
                      <span className="app-member-badge app-member-badge-invited">
                        {_t("pending", "Pending")}
                      </span>
                    </div>
                  </div>
                );
              })}

              {sharedProfiles.map((sp, i) => {
                const landlord = sp.id?.landlordid ?? {};
                const sharedBy = sp.shared_by ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName =
                  `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                  sharedBy.email ||
                  "";
                return (
                  <div key={sp.id?._id ?? i} className="application-member-row">
                    <div className="member-avatar-chip member-avatar-lg member-avatar-chip-gray">
                      {getInitials(landlordName || landlordEmail)}
                    </div>
                    <div className="application-member-info">
                      <p className="application-member-name">{landlordName}</p>
                      <p className="application-member-email">
                        {landlordEmail}
                      </p>
                    </div>
                    {sharedByName && (
                      <div className="app-shared-by-stack">
                        <span className="app-shared-by-label">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── main modal ── */
const ApplicationsModal = ({
  isOpen,
  onClose,
  applications,
  loading,
  onNewApplication,
  onApplicationsChange,
  llShowOnly,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const currentUserEmail = user?.email?.toLowerCase();
  const currentUserId = user?._id;

  const { consentApplication, deleteApplication } =
    useContext(ApplicationContext);
  const [processingId, setProcessingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const handleAccept = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "accepted");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToAcceptRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "rejected");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToRejectRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await deleteApplication(applicationId);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToDeleteApplication")
      );
    } finally {
      setProcessingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal" onClick={onClose}>
      <div
        className={`doc-history-popup total-salary-modal${isOpen ? " open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="drawerIcon" onClick={onClose} />
        <div className="applications-modal-inner">
          <div className="applications-modal-header-block">
            <div
              className="doc-history-header applications-modal-header-row"
              style={{ paddingBottom: "0" }}
            >
              <img
                src={CloseIcon}
                alt=""
                className="doc-history-close-btn"
                onClick={onClose}
                style={{ flexShrink: 0 }}
              />
              <div
                className="d-flex gap-1 flex-column"
                style={{ flex: 1, minWidth: 0 }}
              >
                <h4 className="custom-modal-heading">{_t("applications")}</h4>
                <p className="applications-modal-subtitle">
                  {_t("applicationModelDescription")}
                </p>
              </div>
              {!llShowOnly && (
                <button
                  className="addGuarantorButton d-flex align-items-center justify-content-center applications-modal-new-btn"
                  onClick={() => {
                    onClose();
                    onNewApplication();
                  }}
                  style={{ flexShrink: 0 }}
                >
                  <img src="/images/add-white.svg" alt="" />
                  {_t("newApplication")}
                </button>
              )}
            </div>
          </div>

          <div className="applications-list">
            {loading ? (
              <div className="applications-loader">
                <img
                  src={SkorDefaultLoader}
                  className="defaultSkorLoader"
                  alt="Loading"
                />
              </div>
            ) : (applications ?? []).length === 0 ? (
              <p className="applications-empty-text">
                {_t("noApplicationsYet")}
              </p>
            ) : (
              (applications ?? []).map((app) => (
                <ApplicationCard
                  key={app._id}
                  app={app}
                  currentUserEmail={currentUserEmail}
                  currentUserId={currentUserId}
                  onAccept={handleAccept}
                  onReject={handleReject}
                  onDelete={handleDelete}
                  processingId={processingId}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  onCloseModal={onClose}
                  llShowOnly={llShowOnly}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationsModal;
